/**
 * Derive a single UX state from an article's (status, generation_phases) tuple.
 *
 * The pipeline tracks two orthogonal axes — `status` (workflow gate) and
 * `generation_phases` (which AI stage finished last) — and reviewers need a
 * single "what's happening?" indicator for the UI. This function maps the
 * tuple to one of the values listed in `UiState`.
 *
 * Used by:
 *   - `DirectusAdapter.updateArticle` — auto-computes `ui_state` whenever
 *     `status` or `generation_phases` change in a patch.
 *   - The future custom Directus interface and the SaaS web frontend, which
 *     read `ui_state` directly instead of inspecting the tuple.
 *
 * Adding a new state? Update the `UiState` union, the mapping below, and
 * the `ui_state` field choices in `bin/setup-directus.ts`.
 */

export type UiState =
  // Customer hasn't started yet
  | "idea"
  // AI is working through the first half of the pipeline
  | "ai_researching"      // Scout
  | "ai_outlining"        // Librarian
  | "ai_drafting"         // Writer
  // Human pause: review the draft
  | "awaiting_review"
  // AI is working through the second half of the pipeline
  | "ai_factchecking"     // Auditor
  | "ai_editing"          // Editor
  | "ai_optimizing"       // SEO Optimizer
  | "ai_imaging"          // Art Director
  // Human pause: pick images and approve for publish
  | "awaiting_approval"
  // AI is publishing
  | "ai_publishing"       // Publisher
  // Terminal states
  | "published"
  | "scheduled"
  | "rejected"
  | "failed"
  | "revision_requested"
  // Fiction pipeline (separate stage names)
  | "ai_worldbuilding"
  | "ai_architecting"
  | "ai_drafting_fiction"
  | "ai_continuity_checking"
  | "ai_proseEditing"
  | "ai_illustrating"
  | "fiction_published";

export type ArticleStatus =
  | "proposal"
  | "selected"
  | "in_progress"
  | "waiting_for_review"
  | "waiting_for_approval"
  | "approved"
  | "published"
  | "scheduled"
  | "failed"
  | "rejected"
  | "revision_requested";

export type GenerationPhase =
  | null
  | "phase_1_researched"
  | "phase_2_outlined"
  | "phase_3_draft_generated"
  | "phase_4_fact_checked"
  | "phase_5_corrected"
  | "phase_5b_voice_scored"
  | "phase_6_seo_optimized"
  | "phase_7_art_directed"
  | "phase_8_published"
  | "fiction_phase_1_worldbuilt"
  | "fiction_phase_2_architected"
  | "fiction_phase_3_draft_generated"
  | "fiction_phase_4_continuity_checked"
  | "fiction_phase_5_corrected"
  | "fiction_phase_6_illustrated"
  | "fiction_phase_7_published";

/**
 * Compute the UX state. Status-driven first (terminal + revision states), then
 * phase-driven (which AI stage is currently running or just finished).
 */
export function deriveUiState(
  status: ArticleStatus | string | null | undefined,
  generation_phases: GenerationPhase | string | null | undefined
): UiState {
  // Status-first dispatch — terminal and human-action states win.
  switch (status) {
    case "rejected":
      return "rejected";
    case "failed":
      return "failed";
    case "revision_requested":
      return "revision_requested";
    case "published":
      // Distinguish fiction-published from main pipeline.
      if (generation_phases === "fiction_phase_7_published") return "fiction_published";
      return "published";
    case "scheduled":
      return "scheduled";
    case "waiting_for_review":
      return "awaiting_review";
    case "waiting_for_approval":
      return "awaiting_approval";
    case "approved":
      // Approved articles are about to publish (or just did, but published
      // would have caught that case above). Show as ai_publishing.
      return "ai_publishing";
    case "proposal":
      // Idea state when no phase has run yet.
      if (!generation_phases) return "idea";
      // Proposal with a phase is unusual but possible; fall through to phase logic.
      break;
    case "selected":
    case "in_progress":
    case undefined:
    case null:
      // Phase-driven from here.
      break;
    default:
      // Unknown status — fall through to phase logic, then default to idea.
      break;
  }

  // Phase-driven dispatch — the AI stage currently working.
  // The phase value reflects the LAST completed stage, so the active stage
  // is the one AFTER it in the chain.
  switch (generation_phases) {
    case null:
    case undefined:
    case "":
      return "ai_researching"; // about to start at Scout
    case "phase_1_researched":
      return "ai_outlining"; // Librarian
    case "phase_2_outlined":
      return "ai_drafting"; // Writer
    case "phase_3_draft_generated":
      // Writer just finished — typically status = waiting_for_review (caught above).
      // If still in_progress at this phase, Auditor is running.
      return "ai_factchecking";
    case "phase_4_fact_checked":
      return "ai_editing";
    // FEAT-084 — after the Editor, the voice evaluator runs, not the SEO
    // Optimizer. It is presented as part of editing (the piece is being
    // re-judged on the Editor's own output), matching REVISION_UI_STATE.
    case "phase_5_corrected":
      return "ai_editing";
    case "phase_5b_voice_scored":
      return "ai_optimizing";
    case "phase_6_seo_optimized":
      return "ai_imaging";
    case "phase_7_art_directed":
      // Art Director just finished — typically status = waiting_for_approval (caught above).
      // If still in_progress at this phase, Publisher is running.
      return "ai_publishing";
    case "phase_8_published":
      return "published";

    // Fiction pipeline
    case "fiction_phase_1_worldbuilt":
      return "ai_architecting";
    case "fiction_phase_2_architected":
      return "ai_drafting_fiction";
    case "fiction_phase_3_draft_generated":
      return "ai_continuity_checking";
    case "fiction_phase_4_continuity_checked":
      return "ai_proseEditing";
    case "fiction_phase_5_corrected":
      return "ai_illustrating";
    case "fiction_phase_6_illustrated":
      return "fiction_published"; // about to publish
    case "fiction_phase_7_published":
      return "fiction_published";

    default:
      // Unknown phase — best fallback for an idea or new article.
      return "idea";
  }
}

/**
 * Should this update trigger a `ui_state` recompute? True if the patch contains
 * either `status` or `generation_phases`. Used by the Directus adapter to
 * decide whether to inject `ui_state` into the patch.
 */
export function shouldRecomputeUiState(
  patch: Record<string, unknown>
): boolean {
  return "status" in patch || "generation_phases" in patch;
}

/**
 * Map current ui_state → next status when DecisionBar's "Next Step" is
 * pressed. Returns null when there's nothing to advance to (button
 * disabled). Shared so the API's POST /pieces/:id/decision (FEAT-108 / S-438)
 * computes exactly the step the decision bar offers.
 *
 * BUG-046 — "failed" previously returned null, a dead end that contradicted
 * publisher.ts's own "all targets failed" comment ("mark as failed so the
 * operator can investigate and retry"). status: "failed" is set in exactly
 * one place in the whole pipeline (publisher.ts's all-targets-failed
 * branch), so every "failed" piece is specifically a publish failure —
 * same retry as "ai_publishing" (stuck mid-publish, not yet marked failed).
 */
export function nextStepFor(
  ui: UiState | null | undefined,
  generationPhases?: string | null
): { status: ArticleStatus; hint: string } | null {
  switch (ui) {
    case "awaiting_review":
      // FEAT-005 orchestrator (content.ts) — a review_stages gate at
      // phase_7_art_directed (the LAST content phase before the hard
      // publisher gate) has nothing left to "continue": every phase is
      // already done, so a status="in_progress" run just falls straight
      // through to the hard gate, which requires status==="approved" —
      // not "in_progress" — to actually invoke Publisher. That mismatch
      // bounced the piece straight back to waiting_for_approval with
      // nothing generated, after a confusing flash through the Scheduled
      // column (ai_publishing's ui_state derivation for this exact
      // status+phase pair). Founder-reported on piece 374 (2026-08-11).
      // Any OTHER review stage (phase_1..phase_6) still has real
      // generation work left, so "Continue pipeline" stays correct there.
      if (generationPhases === "phase_7_art_directed") {
        return { status: "approved", hint: "Approve for publish" };
      }
      // Resume a paused review gate — the orchestrator reads generation_phases
      // and skips completed stages automatically.
      return { status: "in_progress", hint: "Continue pipeline" };
    case "awaiting_approval":
      // Approve for publish — status must be "approved" for the publisher
      // gate to pass (orchestrator checks status === "approved").
      return { status: "approved", hint: "Approve for publish" };
    case "ai_publishing":
    case "failed":
      // Piece is stuck mid-publish, or every target already failed —
      // either way, retry from "approved" so the publisher gate re-fires.
      return { status: "approved", hint: "Retry publish" };
    case "rejected":
    case "published":
    case "fiction_published":
    case "scheduled":
      return null;
    default:
      return null;
  }
}
